import useAuthButton from '@/hooks/useAuthButton';
import { ExportOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Table, Tag, message, Space, Typography, Popconfirm, Form, Select, Input } from 'antd';
import React, { useMemo, useRef } from 'react';
import { ProductActions, ProductEvents, ProductInfo, ProductProperties, ProductThingModelType, TypeSpec } from './types';
import { get } from 'lodash';
import { TypeSpecMap } from './helper';
import { DetailModal, DetailModalRef } from './DeatilModal';
import DataModal, { DataModalRef } from './DataModal';
import { deleteThingModel } from '@/api';

const { Text, Link } = Typography;
const { useForm, useWatch } = Form;

const TypeMap: { [key: string]: string } = {
  action: '服务类型',
  event: '事件类型',
  property: '属性类型',
};

interface DefaultDetailProps {
  productInfo: ProductInfo | undefined;
  isCloudProduct: boolean;
  loadProductInfo: () => void;
  productId: string;
}

export const DefaultDetail: React.FC<DefaultDetailProps> = ({ productInfo, isCloudProduct, loadProductInfo, productId }) => {
  const { checkPermissionDisabled } = useAuthButton();
  const [form] = useForm();
  const deatilModalRef = useRef<DetailModalRef>(null);
  const dataModalRef = useRef<DataModalRef>(null);
  const nameValue = useWatch('name', form);
  const typeValue = useWatch('type', form);

  const dataSource = useMemo<Array<ProductThingModelType>>(() => {
    if (!productInfo) return [];

    const { actions, events, properties } = productInfo;
    return [
      ...(actions.map<ProductActions>((item) => ({ ...item, type: 'action' })) || []),
      ...(events.map<ProductEvents>((item) => ({ ...item, type: 'event' })) || []),
      ...(properties.map<ProductProperties>((item) => ({ ...item, type: 'property' })) || []),
    ].filter(({ type, name }) => type.includes(typeValue) && name.includes(nameValue));
  }, [productInfo]);

  return (
    <Card className="m20">
      <Space className="mb15 align-center justify-space-between" wrap>
        <Form layout="inline" form={form} initialValues={{ type: '', name: '' }}>
          {!checkPermissionDisabled('gateway:product:tsl:query') && (
            <>
              <Form.Item key="type" name="type">
                <Select
                  placeholder="请选择云平台"
                  style={{ minWidth: 180 }}
                  options={[
                    { label: '功能类型 (全部)', value: '' },
                    { label: '属性', value: 'property' },
                    { label: '事件', value: 'event' },
                    { label: '服务', value: 'action' },
                  ]}
                />
              </Form.Item>
              <Form.Item key="name" name="name">
                <Input allowClear placeholder="请输入产品名称搜索"></Input>
              </Form.Item>
            </>
          )}
        </Form>
        <Space>
          <Button
            type="primary"
            icon={<ImportOutlined />}
            onClick={() => {
              productId && dataModalRef.current?.open(productId, 'import');
            }}
            disabled={checkPermissionDisabled('gateway:product:tsl:import')}
          >
            导入
          </Button>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={() => {
              productId && dataModalRef.current?.open(productId, 'export');
            }}
            disabled={checkPermissionDisabled('gateway:product:tsl:export')}
          >
            导出
          </Button>
          {productInfo && !isCloudProduct && (
            <Button
              type="primary"
              onClick={() => {
                deatilModalRef.current?.open();
              }}
              disabled={checkPermissionDisabled('gateway:product:tsl:add')}
            >
              <PlusOutlined />
              添加物模型
            </Button>
          )}
        </Space>
      </Space>
      <Table
        dataSource={dataSource}
        bordered
        rowKey="id"
        size="small"
        pagination={{
          size: 'small',
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          total: dataSource.length,
        }}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: '功能类型',
            key: 'type',
            dataIndex: 'type',
            render(value, record) {
              return (
                <>
                  <span>{TypeMap[value as string]}</span>
                  {record?.tag && (
                    <Tag color="default" className="ml10">
                      {record?.tag}
                    </Tag>
                  )}
                </>
              );
            },
          },
          { title: '功能名称', key: 'name', dataIndex: 'name' },
          { title: '标识符', key: 'code', dataIndex: 'code' },
          {
            title: '数据类型',
            key: 'type_spec_type',
            dataIndex: 'type_spec_type',
            render(value, record) {
              const type = get<ProductThingModelType, string, TypeSpec | ''>(record, 'type_spec.type', '') as TypeSpec;
              return TypeSpecMap[type] || '-';
            },
          },
          {
            title: '数据定义',
            key: 'type_spec_specs',
            dataIndex: 'type_spec_specs',
            render(value, record) {
              const type = get<ProductThingModelType, string, TypeSpec | ''>(record, 'type_spec.type', '') as TypeSpec;
              const specs = get<ProductThingModelType, string, string>(record, 'type_spec.specs', '{}') as TypeSpec;

              if (type === 'array') {
                return '数组个数: ' + JSON.parse(specs)?.size;
              } else if (type === 'text') {
                return '数据长度: ' + JSON.parse(specs)?.length;
              } else if (type === 'date') {
                return '整数类型Int64的UTC时间戳 (毫秒)';
              } else if (type === 'bool' || type === 'enum') {
                return Object.entries(JSON.parse(specs))
                  .map(([k, v]) => `${k}-${v}`)
                  .map((text, index) => (
                    <Tag color="processing" key={index}>
                      {text}
                    </Tag>
                  ));
              } else if (type === 'int' || type === 'float') {
                const specsObj = JSON.parse(specs);
                return (
                  <>
                    <div>
                      取值范围: {specsObj?.min}-{specsObj?.max}
                    </div>
                    <div>步长: {specsObj?.step}</div>
                    <div>
                      单位: {specsObj?.unit} ({specsObj?.unitName})
                    </div>
                  </>
                );
              }
              return '-';
            },
          },
          {
            title: '操作',
            key: 'action',
            dataIndex: 'action',
            fixed: 'right',
            render(value, record) {
              return (
                <Space split={<Text type="secondary">/</Text>}>
                  <Link
                    onClick={() => {
                      deatilModalRef.current?.open(record);
                    }}
                  >
                    查看
                  </Link>
                  <Link
                    onClick={() => {
                      deatilModalRef.current?.open(record, true);
                    }}
                  >
                    编辑
                  </Link>
                  <Popconfirm
                    title="此操作将永久删除该记录, 是否继续?"
                    okText="删除"
                    cancelText="取消"
                    placement="topRight"
                    disabled={checkPermissionDisabled('gateway:product:tsl:remove')}
                    onConfirm={() => {
                      deleteThingModel({
                        thing_model_id: record?.id,
                        thing_model_type: record?.type,
                      }).then((resp) => {
                        if (resp.success) {
                          message.success('删除成功');
                          loadProductInfo();
                        } else {
                          message.error(resp?.errorMsg);
                        }
                      });
                    }}
                  >
                    <Typography.Link disabled={checkPermissionDisabled('gateway:product:tsl:remove')}>删除</Typography.Link>
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
      />
      <DetailModal
        ref={deatilModalRef}
        productInfo={productInfo}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
      <DataModal
        ref={dataModalRef}
        onSuccess={() => {
          loadProductInfo();
        }}
      />
    </Card>
  );
};
